import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    IconButton,
    Paper,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    Box,
    Chip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import GetAppIcon from "@material-ui/icons/GetApp";
import SyncIcon from "@material-ui/icons/Sync";

const useStyles = makeStyles((theme) => ({
    dialogHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2),
    },
    attemptHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        color: "#3f51b5",
        fontWeight: "bold",
    },
    finalDocumentsContainer: {
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1),
        overflow: "hidden",
    },
    finalDocumentsHeader: {
        backgroundColor: "#4f5d6d",
        color: "white",
        padding: theme.spacing(1),
        textAlign: "center",
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    documentsList: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    documentCard: {
        padding: theme.spacing(2),
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1),
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
    },
    documentInfo: {
        flexGrow: 1,
    },
    statusBadge: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontSize: "12px",
        height: "24px",
        marginTop: theme.spacing(1),
    },
    verDetalhes: {
        color: "#1976d2",
        cursor: "pointer",
        textDecoration: "none",
        fontWeight: "bold",
        fontSize: "14px",
        display: "block",
        marginTop: theme.spacing(1),
    },
    // Details styles
    detailsContainer: {
        display: "flex",
        gap: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    imagePreviewContainer: {
        flex: 1,
        backgroundColor: "#f5f5f5",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
        borderRadius: theme.spacing(1),
    },
    imagePreview: {
        maxWidth: "100%",
        maxHeight: "600px",
        objectFit: "contain",
    },
    detailsInfo: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    inputLabel: {
        fontSize: "12px",
        color: "#666",
        marginBottom: theme.spacing(0.5),
        fontWeight: "bold",
    },
    inputMock: {
        backgroundColor: "#f5f5f5",
        padding: theme.spacing(1),
        borderRadius: theme.spacing(0.5),
        fontSize: "14px",
    },
    extractedFieldsTitle: {
        fontWeight: "bold",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    tableHeader: {
        backgroundColor: "#f9f9f9",
    },
    tableHeaderCell: {
        fontWeight: "bold",
        fontSize: "12px",
        color: "#666",
    },
    tableCell: {
        fontSize: "13px",
    },
    checkIcon: {
        color: "#7b1fa2", // Based on the purple-ish color in screenshot
    },
    analysisValue: {
        color: "#2e7d32",
        fontWeight: "bold",
    },
    downloadButton: {
        backgroundColor: "#2979ff",
        color: "white",
        "&:hover": {
            backgroundColor: "#1565c0",
        },
        marginBottom: theme.spacing(2),
    },
    closeButtonBottom: {
        backgroundColor: "#2979ff",
        color: "white",
        "&:hover": {
            backgroundColor: "#1565c0",
        },
    },
}));

const TicketHistoryDialog = ({ open, onClose, historyData }) => {
    const classes = useStyles();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isZoomed, setIsZoomed] = useState(false);
    const [transformOrigin, setTransformOrigin] = useState("center center");

    const [parsedHistory, setParsedHistory] = useState(null);

    useEffect(() => {
        if (historyData) {
            let data = historyData.historico_json;
            if (typeof data === "string") {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    console.error("Erro ao parsear historico_json:", e);
                    data = null;
                }
            }
            setParsedHistory(data);
        } else {
            setParsedHistory(null);
        }

        if (!open) {
            setIsZoomed(false);
            setTransformOrigin("center center");
            setSelectedDocument(null);
        }
    }, [open, historyData]);

    const handleMouseMove = (e) => {
        if (!isZoomed) return;
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;
        setTransformOrigin(`${x}% ${y}%`);
    };

    const toggleZoom = () => {
        setIsZoomed(!isZoomed);
        if (isZoomed) {
            setTransformOrigin("center center");
        }
    };

    const handleOpenDetails = (doc) => {
        setSelectedDocument(doc);
    };

    const handleBackToList = () => {
        setSelectedDocument(null);
        setIsZoomed(false);
        setTransformOrigin("center center");
    };

    const renderListView = () => (
        <DialogContent>
            {historyData && (
                <div style={{ marginTop: "13px", backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "5px", display: "flex", flexDirection: "column", gap: "5px", }} className={classes.attemptHeader} >
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%" }}>
                        Hash da viagem: <Typography variant="body1">{historyData?.hash_viagem}</Typography>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%" }}>
                        Data de Inclusão: <Typography variant="body1">{historyData?.dt_inclusao}</Typography>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%" }}>
                        Data de Expiração: <Typography variant="body1">{historyData?.dt_expiracao}</Typography>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%" }}>
                        Processo Ativo: <Typography variant="body1">{historyData?.process_ativo == true ? "Ativo" : "Inativo"}</Typography>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%" }}>
                        Tipo de Validação de Documento: <Typography variant="body1">{historyData?.tp_validacao_documento == "cmp" ? "Composta" : historyData?.tp_validacao_documento == "smp" ? "Simples" : "N/D"}</Typography>
                    </div>
                </div>
            )}
            {/* se parsedHistory for vazio quero imprimir outra coisa */}
            {!parsedHistory && (
                <div style={{ marginTop: "13px", backgroundColor: "#f5f5f5", padding: "5px", borderRadius: "5px", display: "flex", flexDirection: "column", gap: "5px", }} className={classes.attemptHeader} >
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", width: "100%", justifyContent: "center" }}>
                        <Typography variant="body1">Nenhum histórico encontrado</Typography>
                    </div>
                </div>
            )}
            {parsedHistory?.tentativas?.map((tentativa, index) => (
                <div key={index}>
                    <div style={{ marginTop: "13px" }} className={classes.attemptHeader}>
                        <SyncIcon fontSize="small" />
                        <Typography variant="body1">{index + 1} Tentativa</Typography>
                    </div>
                    <div className={classes.finalDocumentsContainer}>
                        <div className={classes.finalDocumentsHeader}>
                            Documentos
                        </div>
                        <div className={classes.documentsList}>
                            {Array.isArray(tentativa) && tentativa.map((doc, docIdx) => (
                                <Paper key={docIdx} elevation={0} className={classes.documentCard}>
                                    <DescriptionIcon style={{ color: "#d1d5db" }} />
                                    <div className={classes.documentInfo}>
                                        <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                            {doc.nome_documento || "Documento sem nome"}
                                        </Typography>
                                        {doc.data_solicitacao && (
                                            <Typography variant="caption" color="textSecondary">
                                                Data: {doc.data_solicitacao}
                                            </Typography>
                                        )}
                                        {doc.resumo_analise && (
                                            <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px', fontStyle: 'italic' }}>
                                                {doc.resumo_analise}
                                            </Typography>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Chip
                                                label={doc.analise_aprovada ? "Aprovado" : "Reprovado"}
                                                className={classes.statusBadge}
                                                style={{
                                                    backgroundColor: doc.analise_aprovada ? "#e8f5e9" : "#ffebee",
                                                    color: doc.analise_aprovada ? "#2e7d32" : "#c62828"
                                                }}
                                            />
                                            {doc.tp_validacao && (
                                                <Typography variant="caption" style={{ marginTop: '8px' }}>
                                                    ({doc.tp_validacao === 'ia_cmp' ? 'IA Composta' : doc.tp_validacao === 'ia_smp' ? 'IA Simples' : doc.tp_validacao})
                                                </Typography>
                                            )}
                                        </div>
                                        <div
                                            className={classes.verDetalhes}
                                            onClick={() => handleOpenDetails(doc)}
                                        >
                                            Ver Detalhes
                                        </div>
                                    </div>
                                </Paper>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </DialogContent>
    );

    const renderDetailsView = () => (
        <DialogContent>
            <div className={classes.detailsContainer}>
                <div className={classes.imagePreviewContainer}>
                    {selectedDocument.url_arquivo ? (
                        <img
                            src={selectedDocument.url_arquivo}
                            alt={selectedDocument.nome_documento}
                            className={classes.imagePreview}
                            onMouseMove={handleMouseMove}
                            onClick={toggleZoom}
                            onMouseLeave={() => isZoomed && setIsZoomed(false)}
                            style={{
                                cursor: isZoomed ? "zoom-out" : "zoom-in",
                                transform: isZoomed ? "scale(2.5)" : "scale(1)",
                                transformOrigin: transformOrigin,
                                transition: isZoomed ? "none" : "transform 0.3s ease",
                                zIndex: 10,
                                position: 'relative'
                            }}
                        />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <DescriptionIcon style={{ fontSize: '100px', color: '#d1d5db' }} />
                            <Typography variant="body2" color="textSecondary">
                                Imagem não disponível para este registro
                            </Typography>
                        </div>
                    )}
                </div>
                <div className={classes.detailsInfo}>
                    {selectedDocument.url_arquivo && (
                        <Button
                            variant="contained"
                            style={{ maxWidth: "200px" }}
                            className={classes.downloadButton}
                            startIcon={<GetAppIcon />}
                            href={selectedDocument.url_arquivo}
                            target="_blank"
                        >
                            Baixar Imagem
                        </Button>
                    )}

                    <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: theme => theme.spacing(1) }}>
                        Análise do Documento
                    </Typography>

                    <div>
                        <div className={classes.inputLabel}>Nome do Arquivo</div>
                        <div className={classes.inputMock}>{selectedDocument.file_name}</div>
                    </div>

                    <div>
                        <div className={classes.inputLabel}>Tipo de Validação</div>
                        <div className={classes.inputMock}>{selectedDocument.tp_validacao == "ia_cmp" ? "Composta" : selectedDocument.tp_validacao == "ia_smp" ? "Simples" : "N/D"}</div>
                    </div>

                    <div>
                        <div className={classes.inputLabel}>Resumo da Análise</div>
                        <div className={classes.inputMock}>{selectedDocument.resumo_analise}</div>
                    </div>

                    {selectedDocument.fields && selectedDocument.fields.length > 0 && (
                        <>
                            <Typography variant="subtitle1" className={classes.extractedFieldsTitle}>
                                Campos Extraídos
                            </Typography>
                            <TableContainer component={Paper} elevation={0} variant="outlined">
                                <Table size="small">
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell className={classes.tableHeaderCell}>Situação</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Campo</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Info. Cliente</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Info. Análise</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedDocument.fields.map((field, fIdx) => (
                                            <TableRow key={fIdx}>
                                                <TableCell className={classes.tableCell}>
                                                    <CheckCircleIcon className={classes.checkIcon} fontSize="small" />
                                                </TableCell>
                                                <TableCell className={classes.tableCell} style={{ fontWeight: "bold" }}>
                                                    {field.cliente.type}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {field.cliente.mentionText}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    <span className={classes.analysisValue}>
                                                        {field.analise.mentionText}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </div>
            </div>
        </DialogContent>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={selectedDocument ? "lg" : "sm"}
            fullWidth
        >
            <div className={classes.dialogHeader}>
                <Typography
                    variant="h6"
                    style={{
                        fontWeight: 600,
                        fontSize: window.innerWidth < 600 ? "1rem" : "1.25rem"
                    }}
                >
                    {selectedDocument ? `Detalhes: ${selectedDocument.nome_documento}` : "Histórico do Usuário"}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </div>
            <Divider />

            {selectedDocument ? renderDetailsView() : renderListView()}

            <DialogActions>
                {selectedDocument ? (
                    <Button
                        onClick={handleBackToList}
                        variant="contained"
                        className={classes.closeButtonBottom}
                    >
                        Voltar
                    </Button>
                ) : (
                    <Button onClick={onClose} color="primary" variant="contained">
                        Fechar
                    </Button>
                )}
                {selectedDocument && (
                    <Button onClick={onClose} variant="contained" className={classes.closeButtonBottom}>
                        Fechar
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TicketHistoryDialog;
